import { useContext, useEffect, useState } from 'react'
import { Container, Divider, Grid, Typography } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import moment from 'moment'

import CommonBackButton from '../../atoms/CommonBackButton/CommonBackButton'
import { Route } from '../../../router'
import { AppContext } from '../../../stores/App/AppStore'
import { UserContext } from '../../../stores/User/UserStore'
import { DatePatterns } from '../../../types/date-patterns'
import { StripePaymentModal } from '../../molecules/StripeModal/StripePaymentModal'
import ConfirmationDialog from '../../molecules/ConfirmationDialog/ConfirmationDialog'
import TermsAndConditionsCheckbox from '../../organisms/TermsAndConditionsCheckbox/TermsAndConditionsCheckbox'
import { logAction } from '../../../clients/logger'
import { errorPopupMessageTemplate, generateErrorCode, paymentPageMessageTemplate } from '../../../helpers/logging/templates'
import useTrackPageView from "../../../helpers/hooks/use-track-page-view"
import PaymentSummaryDetailsTotal from '../../atoms/PaymentSummaryDetailsTotal/PaymentSummaryDetailsTotal'

import styles from './BundlePaymentPage.module.css'
import { useBundleRepository } from '../../../repositories/Bundle/use-bundle-repository'
import PaymentSummarySkeleton from '../Membership/PaymentSummary/PaymentSummary.skeleton'

const DECIMALS = 2

const BundlePaymentPage = () => {
    useTrackPageView(`Bundle Payment Page`)

    const { t } = useTranslation()

    const navigate = useNavigate()
    const { retrieveBundle, retrieveBundleCode, updateMembershipFromNonPaymentBundle, createBundlePaymentOrder, updateMembershipAfterPaymentForBundle } = useBundleRepository()

    const { state: appState } = useContext(AppContext)
    const { state: userState } = useContext(UserContext)

    const [formStarterTermsConsent, setFormStarterTermsConsent] = useState(false)
    const [dialogErrorMessageTitle, setDialogErrorMessageTitle] = useState(t(`sign_up_failure`))
    const [dialogErrorMessage, setDialogErrorMessage] = useState(``)
    const [isErrorDialogOpen, setIsErrorDialogOpen] = useState(false)
    const [stripeClientSecret, setStripeClientSecret] = useState(``)
    const [stripePaymentIntentId, setStripePaymentIntentId] = useState(``)

    const userEmail = userState.membership?.data?.emailAddress
    const bundleCode = retrieveBundleCode()

    useEffect(() => {
        if (!appState.pages.bundle && bundleCode) {
            retrieveBundle(bundleCode)
        }
    }, [bundleCode])

    useEffect(() => {
        if (bundleCode && appState.pages.bundle){
            if (appState.pages.bundle?.paymentOptions != `paymentUpfront`) {
                updateMembershipFromNonPaymentBundle(bundleCode).then(() => {
                    navigate(Route.MEMBERSHIP_SIGN_UP)
                })
            }else{
                createBundlePaymentOrder(bundleCode).then(bundlePO => {
                    setStripeClientSecret(bundlePO.clientSecret)
                    setStripePaymentIntentId(bundlePO.paymentIntentId)
                })
            }
        }
    }, [appState.pages.bundle])

    useEffect(() => {
        logAction(`error`, errorPopupMessageTemplate(dialogErrorMessage, userState.membership?.data?.id?.toString() ?? `not found`))
    }, [dialogErrorMessage])

    const handleOnSuccess = () => {
        logAction(`debug`, paymentPageMessageTemplate(appState.paymentOrder.data?.firstPaymentAmount.toString(), userState.membership?.data, userState.user?.data))
        updateMembershipAfterPaymentForBundle(bundleCode ?? ``, stripePaymentIntentId).then(() => {
            navigate(Route.MEMBERSHIP_SIGN_UP)
        })
    }

    const handleOnStarterTermsConsentChange = (starterTermsConsent: boolean) => {
        setFormStarterTermsConsent(starterTermsConsent)
    }

    const showConsentDialog = () => {
        setDialogErrorMessageTitle(t(`sign_up_failure_terms_and_conditions`))
        setDialogErrorMessage(t(`sign_up_terms_and_conditions`) + ` error code: ${generateErrorCode()}`)
        setIsErrorDialogOpen(true)
    }

    const handleErrorDialogClose = () => {
        setIsErrorDialogOpen(false)
    }

    const handleBackButtonClick = () => {
        window.location.assign(`${window.location.origin}`)
    }

    const paymentAmountFormatter = (paymentAmount: number): string => {
        return paymentAmount.toFixed(DECIMALS)
    }

    const dateStringFormatter = (dateString: string | Date): string => {
        return moment(dateString).format(DatePatterns.DATE)
    }

    if (!stripeClientSecret) {
        return <PaymentSummarySkeleton />
    }

    const totalAmount = (appState.pages.bundle?.NTUCPrice ?? 0) + (appState.pages.bundle?.clubPrice ?? 0) + (appState.pages.bundle?.bundleExtraPrice ?? 0)
    const startDate = userState.membership?.data?.signUpDate ?? new Date()
    // eslint-disable-next-line no-magic-numbers
    const endDate = moment(startDate).clone().add((appState.pages.bundle?.numberOfYears ?? 1) - 1, `year`).add(11, `month`).endOf(`month`).toDate()

    const paymentSummaryAmount = paymentAmountFormatter(totalAmount)
    const formattedStartDate = dateStringFormatter(startDate)
    const formattedEndDate = dateStringFormatter(endDate)

    return (
        <Container>
            <CommonBackButton onClick={handleBackButtonClick} />

            <Grid container className={styles.gridContainer}>

                <Grid className={styles.paymentSummaryHeading}>
                    <Typography variant={`h5`} mb={1}>{t(`payment_summary_title`)}</Typography>
                    <Typography variant={`body1`}>{t(`payment_summary_subtitle`)}</Typography>
                </Grid>

                <Grid className={styles.paymentSummaryDetails}>
                    <Typography variant={`h6`} mb={2}>{t(`payment_summary_details`)}</Typography>
                    <Grid container direction={`row`} justifyContent={`space-between`} alignItems={`start`}>
                        <Grid className={styles.paymentSummaryDetailsLeftCol}>
                            <Typography variant={`subtitle1`}>{appState.pages.bundle?.name}</Typography>
                            <Typography variant={`body2`} mb={1}>{t(`payment_summary_subscription_duration`, { start: formattedStartDate, end: formattedEndDate })}</Typography>
                        </Grid>
                        <Typography variant={`subtitle1`}>{t(`payment_summary_amount`, { amount: paymentSummaryAmount })}</Typography>
                    </Grid>

                    <Divider className={styles.paymentSummaryDetailsDivider} />

                    <PaymentSummaryDetailsTotal paymentSummaryAmount={paymentSummaryAmount}/>

                    <Grid container paddingTop={4} direction={`row`}>
                        <TermsAndConditionsCheckbox starterTemsConsent={formStarterTermsConsent} onTermsConsentChange={handleOnStarterTermsConsentChange} />
                    </Grid>

                    <Grid container paddingTop={4} direction={`row`}>
                        {stripeClientSecret &&
                            <StripePaymentModal onConsentFalse={showConsentDialog} consent={formStarterTermsConsent} clientSecret={stripeClientSecret} email={userEmail} isFirstYearFree={false} isSubscriptionGiftPurchase={false} isSubscriptionGiftRedemption={false} isBundlePurchase={true} onSuccess={handleOnSuccess} stripePaymentIntentId={stripePaymentIntentId} />
                        }
                    </Grid>
                </Grid>
            </Grid>

            <ConfirmationDialog
                buttonText={t(`common_button_confirmation`)}
                confirmationTitle={dialogErrorMessageTitle}
                confirmationText={dialogErrorMessage}
                dismissButtonText={t(`common_back_to_home`)}
                defaultOpen={isErrorDialogOpen}
                onConfirm={handleErrorDialogClose}
                onDismiss={() => navigate(Route.ROOT)}
            />
        </Container>
    )
}

export default BundlePaymentPage
